import type { ButtonHTMLAttributes, MouseEvent, ReactNode, Ref } from 'react'
import { cn } from '../cn'
import { Spinner } from './Spinner'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

const BASE =
  'inline-flex shrink-0 select-none items-center justify-center gap-2 whitespace-nowrap rounded-md font-semibold transition-[background-color,border-color,color,box-shadow] duration-150 disabled:cursor-not-allowed disabled:opacity-55 aria-disabled:cursor-progress'

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'border border-teal bg-teal text-white shadow-[0_1px_0_rgb(255_255_255/0.12)_inset,0_1px_2px_rgb(22_62_58/0.18)] hover:border-teal-800 hover:bg-teal-800 active:bg-teal-900',
  secondary:
    'border border-line-strong bg-surface text-teal hover:border-teal-300 hover:bg-wash active:bg-wash-strong',
  ghost: 'border border-transparent bg-transparent text-teal hover:bg-wash active:bg-wash-strong',
  danger:
    'border border-danger bg-danger text-white hover:border-[#7f2f1d] hover:bg-[#7f2f1d] active:bg-[#6a2718]',
}

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-[13px]',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-5 text-[15px]',
}

const ICON_SIZES: Record<ButtonSize, string> = {
  sm: 'size-8',
  md: 'size-10',
  lg: 'size-12',
}

export interface ButtonStyleOptions {
  variant?: ButtonVariant
  size?: ButtonSize
  fullWidth?: boolean
  className?: string
}

/** Button classes, for router links that should look like buttons. */
export function buttonClasses({
  variant = 'primary',
  size = 'md',
  fullWidth,
  className,
}: ButtonStyleOptions = {}): string {
  return cn(BASE, VARIANTS[variant], SIZES[size], fullWidth && 'w-full', className)
}

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    Omit<ButtonStyleOptions, 'className'> {
  /** Shows a spinner, sets `aria-busy` and ignores clicks while keeping focus on the button. */
  loading?: boolean
  /** Icon before the label (decorative). */
  icon?: ReactNode
  /** Icon after the label (decorative). */
  trailingIcon?: ReactNode
  ref?: Ref<HTMLButtonElement>
}

export function Button({
  variant,
  size,
  fullWidth,
  loading = false,
  icon,
  trailingIcon,
  className,
  children,
  type = 'button',
  onClick,
  ...rest
}: ButtonProps) {
  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    if (loading) {
      event.preventDefault()
      return
    }
    onClick?.(event)
  }

  return (
    <button
      type={type}
      className={buttonClasses({ variant, size, fullWidth, className })}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      onClick={handleClick}
      {...rest}
    >
      {loading ? <Spinner /> : icon ? <Decorative>{icon}</Decorative> : null}
      {children}
      {trailingIcon ? <Decorative>{trailingIcon}</Decorative> : null}
    </button>
  )
}

function Decorative({ children }: { children: ReactNode }) {
  return (
    <span aria-hidden="true" className="inline-flex [&>svg]:size-4">
      {children}
    </span>
  )
}

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'aria-label'> {
  /** Accessible name, required because the button has no visible text. */
  label: string
  icon: ReactNode
  variant?: ButtonVariant
  size?: ButtonSize
  ref?: Ref<HTMLButtonElement>
}

export function IconButton({
  label,
  icon,
  variant = 'ghost',
  size = 'md',
  className,
  type = 'button',
  title,
  ...rest
}: IconButtonProps) {
  return (
    <button
      type={type}
      aria-label={label}
      title={title ?? label}
      className={cn(BASE, VARIANTS[variant], ICON_SIZES[size], 'p-0', className)}
      {...rest}
    >
      <span aria-hidden="true" className="inline-flex [&>svg]:size-[18px]">
        {icon}
      </span>
    </button>
  )
}
